import { Box, Button, TextField } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import EventLogFilterDTO from '../../../adapters/dto/eventLog/EventLogFilterDTO';
import UserDTO from '../../../adapters/dto/user/UserDTO';
import EventLogGroupAutoComplete from '../../../components/eventLog/EventLogGroupAutoComplete';
import EventLogNameAutoComplete from '../../../components/eventLog/EventLogNameAutoComplete';
import UserAutoComplete from '../../../components/user/UserAutoComplete';
import dayjs from 'dayjs';

interface Props {
  search(filter: EventLogFilterDTO): void;
}

const StyledFilter = styled(Box)(({ theme }) => ({
  width: '100%',
  paddingLeft: '20px',
}));

const StyledRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
}));

const StyledButton = styled(Button)(({ theme }) => ({
  marginLeft: '10px',
}));

interface FilterFormData {
  from: Date;
  to: Date;
  username: string;
}

function Filter({ search }: Props) {
  const [user, setUser] = useState<UserDTO | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [selectedName, setSelectedName] = useState<string | null>(null);
  useEffect(() => {
    if (!selectedGroup) {
      setSelectedName(null);
    }
  }, [selectedGroup]);

  const { handleSubmit, register, reset } = useForm<FilterFormData>({
    mode: 'all',
  });

  const updateSelectedGroup = (group: string) => {
    setSelectedName(null);
    setSelectedGroup(group);
  };

  const updateUser = (user: UserDTO | null) => {
    setUser(user);
  };

  const onSubmit = handleSubmit(async (data) => {
    const filter: EventLogFilterDTO = {
      to: data.to
        ? dayjs(data.to).isValid()
          ? dayjs(data.to).toDate()
          : undefined
        : undefined,
      from: data.from
        ? dayjs(data.from).isValid()
          ? dayjs(data.from).toDate()
          : undefined
        : undefined,
      group: selectedGroup ? selectedGroup : undefined,
      userId: user ? user.id : undefined,
      name: selectedName ? selectedName : undefined,
    };
    search(filter);
  });

  const resetData = () => {
    reset({ from: undefined, to: undefined, username: '' });
    setSelectedGroup(null);
    setSelectedName(null);
    setUser(null);
  };

  return (
    <Box
      sx={(theme) => ({
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: theme.spacing(2),
      })}>
      <Box sx={{ width: '100%' }}>
        <StyledRow>
          <StyledFilter>
            <TextField
              autoFocus
              margin="dense"
              name="from"
              label="From"
              type="datetime-local"
              {...register}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </StyledFilter>
          <StyledFilter>
            <TextField
              autoFocus
              margin="dense"
              name="to"
              label="To"
              type="datetime-local"
              {...register}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </StyledFilter>
          <StyledFilter>
            <UserAutoComplete
              selection={updateUser}
              selectedUser={user}
              role={undefined}
              label="User"
              removeCurrentUser={false}
              id="assignUser"
              showInativeUsers={true}
            />
          </StyledFilter>
        </StyledRow>
        <StyledRow>
          <StyledFilter>
            <EventLogGroupAutoComplete
              selection={updateSelectedGroup}
              selected={selectedGroup}
            />
          </StyledFilter>
          <StyledFilter>
            <EventLogNameAutoComplete
              selection={setSelectedName}
              selected={selectedName}
              group={selectedGroup}
            />
          </StyledFilter>
          <Box sx={{ display: 'flex', height: '32px' }}>
            <StyledButton
              variant="contained"
              color="secondary"
              size="small"
              onClick={onSubmit}>
              Search
            </StyledButton>
            <StyledButton
              variant="outlined"
              color="secondary"
              size="small"
              onClick={resetData}>
              Reset
            </StyledButton>
          </Box>
        </StyledRow>
      </Box>
    </Box>
  );
}

export default Filter;
